import React, { useState, useEffect } from "react";
import companyService from "@/services/companyService";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";

interface SOC2State {
  enabled: "enabled" | "disabled";
}

const defaultSOC2: SOC2State = {
  enabled: "disabled",
};

export default function SOC2Module() {
  const [soc2, setSoc2] = useState<SOC2State>(defaultSOC2);
  const [soc2Initial, setSoc2Initial] = useState<SOC2State | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchSOC2 = async () => {
      setIsLoading(true);
      try {
        const settings = await companyService.getCompanySettings(undefined, "complianceModules");
        const soc2Settings = settings?.["soc2"] ?? {};
        // Precedence: 'Enabled' (case-insensitive), then 'active', 'enabled', 'Active', etc.
        let isEnabled = false;
        // Helper to check both string and boolean
        function checkEnabled(val: unknown): boolean {
          if (typeof val === 'boolean') return val;
          if (typeof val === 'string') return val.toLowerCase() === 'true' || val.toLowerCase() === 'enabled';
          return false;
        }
        if (typeof soc2Settings.Enabled !== 'undefined') {
          isEnabled = checkEnabled(soc2Settings.Enabled);
        } else if (typeof soc2Settings.enabled !== 'undefined') {
          isEnabled = checkEnabled(soc2Settings.enabled);
        } else if (typeof soc2Settings.active !== 'undefined') {
          isEnabled = checkEnabled(soc2Settings.active);
        } else if (typeof soc2Settings.Active !== 'undefined') {
          isEnabled = checkEnabled(soc2Settings.Active);
        }
        setSoc2({ enabled: isEnabled ? "enabled" : "disabled" });
        setSoc2Initial({ enabled: isEnabled ? "enabled" : "disabled" });
      } catch (err) {
        toast.error("Failed to fetch SOC 2 settings");
      } finally {
        setIsLoading(false);
      }
    };
    fetchSOC2();
  }, []);

  const soc2Changed = soc2Initial && JSON.stringify(soc2) !== JSON.stringify(soc2Initial);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await companyService.saveCompanySettings(undefined, "complianceModules", {
        soc2: { Enabled: soc2.enabled === "enabled" },
      });
      setSoc2Initial({ ...soc2 });
      toast.success("SOC 2 settings saved");
    } catch (err) {
      toast.error("Failed to save SOC 2 settings");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-muted/30 p-6 rounded-lg border border-border space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-green-500/10 p-3 rounded-full">
          <span role="img" aria-label="SOC 2">🏢</span>
        </div>
        <div>
          <div className="font-semibold text-lg">SOC 2</div>
          <div className="text-xs text-muted-foreground">Service Organization Control 2 Module</div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Module Status</label>
        <Select
          value={soc2.enabled}
          onValueChange={val => setSoc2(v => ({ ...v, enabled: val as SOC2State["enabled"] }))}
          disabled={isLoading || isSaving}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="enabled">Enabled</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-2 mt-4">
        <Button onClick={handleSave} disabled={!soc2Changed || isSaving || isLoading}>
          {isSaving ? "Saving..." : "Save SOC 2 Settings"}
        </Button>
      </div>
    </div>
  );
}
